import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { LayoutService, NotiSidebarEvent } from '../services/layout.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-notification-sidebar',
  templateUrl: './notification-sidebar.component.html',
  styleUrls: ['./notification-sidebar.component.scss']
})
export class NotificationSidebarComponent implements OnInit, OnDestroy {

  private layoutSub!: Subscription;

  isOpen = false;
  selectedMethod: 'cash' | 'card' | 'upi' | null = null;
  resultStatus: 'success' | 'failed' | null = null;
  resultTitle = '';
  resultAmount: number | null = null;
  amount: number | null = null;
  // additional payload fields (optional)
  payload: any = null;

  constructor(private layoutService: LayoutService, public toastr: ToastrService, ) { }

  ngOnInit(): void {
    // subscribe to the notifier events (open/close + payload)
    this.layoutSub = this.layoutService.toggleNotiSidebar$.subscribe(
      (event: NotiSidebarEvent) => {
        this.isOpen = !!event.open;
        this.payload = event.payload ?? null;

        // try to extract amount if present
        if (this.payload && typeof this.payload.amount !== 'undefined') {
          const parsed = Number(this.payload.amount);
          this.amount = isNaN(parsed) ? null : parsed;
        } else {
          this.amount = null;
        }

        // optional: if payload includes default method, set it
        if (this.payload && this.payload.defaultMethod) {
          const m = this.payload.defaultMethod;
          if (m === 'cash' || m === 'card' || m === 'upi') {
            this.selectedMethod = m;
          }
        }

        // reset result UI when opening
        if (this.isOpen) {
          this.resultStatus = null;
          this.resultTitle = '';
          this.resultAmount = null;
        }
      }
    );
  }

  ngOnDestroy(): void {
    if (this.layoutSub) {
      this.layoutSub.unsubscribe();
    }
  }

  onClose(): void {
    // close and clear payload locally (service emits closed event with no payload)
    this.layoutService.toggleNotificationSidebar(false);
    this.resetLocalState();
  }

  /**
   * Called when the user confirms a cash payment.
   * Emits a paymentConfirmed event, shows a toast & the success UI,
   * then closes the sidebar after a short delay.
   */
  // notification-sidebar.component.ts (partial)
  confirmCashPayment() {
    // prepare payload. include source (if present) so recipients can branch logic
    const payload = {
      orderId: this.payload?.orderId ?? null,
      paymentMode: 'Cash',
      transactionNo: '',
      amount: this.amount ?? null,
      source: this.payload?.source ?? null // <-- forward source
    };

    // notify other parts of app (OrdersComponent)
    this.layoutService.notifyPaymentConfirmed(payload);

    // local UX: show toast & UI result
    this.toastr.clear();
    this.toastr.success('Cash payment recorded', 'Payment', { positionClass: 'toast-top-right' });

    this.resultStatus = 'success';
    this.resultTitle = 'Payment Successful';
    this.resultAmount = this.amount ?? null;

    window.setTimeout(() => {
      this.layoutService.toggleNotificationSidebar(false);
      this.resetLocalState();
    }, 1100);
  }

  onCashDone() {
    this.resultStatus = 'success';
    this.resultTitle = 'Payment Successful';
    this.resultAmount = this.amount ?? null;
  }

  onMethodClick(method: 'cash' | 'card' | 'upi'): void {
    this.selectedMethod = method;
  }

  private resetLocalState(): void {
    this.selectedMethod = null;
    this.amount = null;
    this.payload = null;
    this.isOpen = false;
    this.resultStatus = null;
    this.resultTitle = '';
    this.resultAmount = null;
  }
}
